import { useMemo } from 'react';
import { Center, Divider, Spinner } from 'src/components/atoms';
import { DynamicQuestion } from 'src/components/molecules/DynamicQuestion/DynamicQuestion';
import { DynamicQuestionAnswerReadOnlyFallback } from 'src/components/molecules/DynamicQuestion/DynamicQuestionAnswerReadOnlyFallback';
import {
  DisclosureCaseByIdQuery,
  useGetQuestionnaireQuestionsQuery,
} from 'src/graphql';
import { buildOptionsByQuestionId } from 'src/utils/formatDynamicQuestionAnswerDisplay';
import {
  buildDynamicQuestionsByQuestionId,
  mergeQuestionnaireRowsForReadOnlyDisplay,
  normalizeDynamicQuestionAnswerValue,
} from 'src/utils/normalizeDynamicQuestionAnswerValue';
import {
  Description,
  EmptyStateContainer,
  ResponseContainer,
  ResponseTitle,
  Title,
} from './ReportDynamicQuestionsContainer.styles';

export const ReportDynamicQuestionsContainer = ({
  reportId,
  qaData,
  isQaLoading,
  isQaError,
}: {
  reportId: string;
  qaData?: DisclosureCaseByIdQuery;
  isQaLoading?: boolean;
  isQaError?: boolean;
}) => {
  const questionnaireId = qaData?.disclosureCaseById?.questionnaireId ?? '';
  const { data: questionsData } = useGetQuestionnaireQuestionsQuery(
    { questionnaireId },
    { enabled: Boolean(questionnaireId) },
  );
  const optionsByQuestionId = useMemo(
    () =>
      buildOptionsByQuestionId(
        questionsData?.questionnaireQuestions?.questions ?? undefined,
      ),
    [questionsData?.questionnaireQuestions?.questions],
  );
  const questionsByQuestionId = useMemo(
    () =>
      buildDynamicQuestionsByQuestionId(
        questionsData?.questionnaireQuestions?.questions,
      ),
    [questionsData?.questionnaireQuestions?.questions],
  );
  const readOnlyRows = useMemo(
    () =>
      mergeQuestionnaireRowsForReadOnlyDisplay(
        questionsData?.questionnaireQuestions?.questions,
        qaData?.disclosureCaseById?.answers,
      ),
    [
      questionsData?.questionnaireQuestions?.questions,
      qaData?.disclosureCaseById?.answers,
    ],
  );

  if (isQaLoading) {
    return (
      <Center data-testid="dynamic-questions-section-loading-state">
        <Spinner variant="secondary" size="large" />
      </Center>
    );
  }

  if (isQaError) {
    return (
      <EmptyStateContainer data-testid="dynamic-questions-section-error-state">
        <Divider variant="horizontal" />
        <Title>Error loading questions details</Title>
        <Description>There was an issue retrieving more details</Description>
      </EmptyStateContainer>
    );
  }

  if (readOnlyRows.length > 0) {
    return (
      <ResponseContainer>
        <ResponseTitle>Misconduct responses</ResponseTitle>
        {readOnlyRows.map(({ question, answerValue }, index) => {
          const questionId = question.id ?? '';
          const resolvedQuestion =
            questionsByQuestionId[questionId] ?? question;
          const typeKey = resolvedQuestion?.questionType?.typeKey;
          const useDynamicQuestion =
            resolvedQuestion != null &&
            typeKey != null &&
            String(typeKey).trim() !== '';
          const dataTestId = `dynamic-questions-section-answer-${index}`;
          if (useDynamicQuestion) {
            return (
              <DynamicQuestion
                key={questionId !== '' ? questionId : index}
                readOnly
                question={resolvedQuestion}
                value={normalizeDynamicQuestionAnswerValue(answerValue)}
                reportIdForFileDownload={reportId !== '' ? reportId : null}
                fileDownloadCaseManagerView
                onChange={() => {}}
                data-testid={dataTestId}
              />
            );
          }
          return (
            <DynamicQuestionAnswerReadOnlyFallback
              key={questionId !== '' ? questionId : index}
              questionText={resolvedQuestion.questionText ?? ''}
              description={resolvedQuestion.description}
              answerValue={answerValue}
              formatOptions={optionsByQuestionId[questionId] ?? undefined}
              reportId={reportId}
              fileDownloadCaseManagerView
              data-testid={dataTestId}
            />
          );
        })}
      </ResponseContainer>
    );
  }

  return (
    <EmptyStateContainer data-testid="dynamic-questions-section-empty-state">
      <Divider variant="horizontal" />
      <Title>No data available</Title>
      <Description>
        There are no further details available for this case
      </Description>
    </EmptyStateContainer>
  );
};
